import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/button'

export function RecoveryCodes({
  codes,
  onSaved,
}: {
  codes: string[]
  onSaved: () => void
}) {
  const { t } = useTranslation()
  const [saved, setSaved] = useState(false)
  const [notice, setNotice] = useState('')
  const heading = useRef<HTMLHeadingElement>(null)
  useEffect(() => {
    heading.current?.focus()
  }, [])
  return (
    <section
      className="sentry-block flex min-w-0 flex-col gap-4"
      data-sentry-block="true"
      aria-labelledby="recovery-title"
    >
      <h2
        id="recovery-title"
        ref={heading}
        tabIndex={-1}
        className="text-xl font-medium"
      >
        {t('security.recoveryTitle')}
      </h2>
      <p>{t('security.recoveryOnce')}</p>
      <ul className="grid grid-cols-1 gap-3 rounded-lg border border-neutralColor5 p-4 font-mono sm:grid-cols-2">
        {codes.map((code) => (
          <li key={code} className="break-all">
            {code}
          </li>
        ))}
      </ul>
      <div className="flex flex-wrap gap-3">
        <Button
          variant="secondary"
          type="button"
          onClick={async () => {
            try {
              await navigator.clipboard.writeText(codes.join('\n'))
              setNotice('security.copied')
            } catch {
              setNotice('security.copyFailed')
            }
          }}
        >
          {t('security.copyCodes')}
        </Button>
        <Button
          variant="secondary"
          type="button"
          onClick={() => {
            let url: string | undefined
            try {
              url = URL.createObjectURL(
                new Blob([codes.join('\n')], { type: 'text/plain' }),
              )
              const link = document.createElement('a')
              link.href = url
              link.download = 'wecheck-recovery-codes.txt'
              document.body.appendChild(link)
              link.click()
              link.remove()
              setNotice('security.downloadStarted')
            } catch {
              setNotice('security.downloadFailed')
            } finally {
              if (url) {
                const downloadUrl = url
                setTimeout(() => URL.revokeObjectURL(downloadUrl), 1000)
              }
            }
          }}
        >
          {t('security.downloadCodes')}
        </Button>
      </div>
      {notice && <p role="status">{t(notice)}</p>}
      <label className="flex items-start gap-3 text-sm">
        <input
          type="checkbox"
          checked={saved}
          onChange={(e) => setSaved(e.target.checked)}
          className="mt-1 h-4 w-4"
        />
        {t('security.savedAcknowledgement')}
      </label>
      <Button
        type="button"
        disabled={!saved}
        onClick={onSaved}
        className="self-start"
      >
        {t('security.done')}
      </Button>
    </section>
  )
}
